import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { ArrowDown, ArrowUpRight, MapPin, Phone, Mail, MessageCircle } from "lucide-react";
import { MaskedLine, FadeUp, Eyebrow } from "../components/Reveal";
import LeadForm from "../components/LeadForm";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { CONTACT } from "../content";
import { useEnquiry } from "../enquiryContext";
import { scrollToId } from "../hooks/useLenis";

function ProjectHero({ project }) {
  const [slide, setSlide] = useState(0);
  const { scrollY } = useScroll();
  const bgY = useTransform(scrollY, [0, 900], [0, 240]);
  const { openPopup } = useEnquiry();

  useEffect(() => {
    setSlide(0);
    const t = setInterval(() => setSlide((s) => (s + 1) % project.heroImages.length), 5200);
    return () => clearInterval(t);
  }, [project]);

  return (
    <section data-testid="project-hero" className="relative h-[100svh] min-h-[620px] overflow-hidden bg-ink">
      <motion.div style={{ y: bgY }} className="absolute inset-0 scale-[1.15]">
        <AnimatePresence mode="sync">
          <motion.img
            key={`${project.slug}-${slide}`}
            src={project.heroImages[slide]}
            alt={project.name}
            className="absolute inset-0 h-full w-full object-cover"
            initial={{ opacity: 0, scale: 1.06 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.6, ease: "easeOut" }}
          />
        </AnimatePresence>
      </motion.div>
      <div className="absolute inset-0 bg-black/60" />
      <div className="absolute inset-0 bg-gradient-to-t from-ink via-transparent to-ink/40" />

      <div className="relative z-10 h-full max-w-7xl mx-auto px-5 md:px-10 flex flex-col justify-end pb-28 md:pb-24">
        <motion.p
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35, duration: 0.8 }}
          className="text-[11px] md:text-xs font-medium uppercase tracking-editorial text-gold mb-6"
        >
          {project.eyebrow}
        </motion.p>

        <h1 className="font-serif-display text-[#F9F8F6] text-6xl sm:text-7xl lg:text-9xl leading-[0.98] font-medium">
          <span className="line-mask">
            <motion.span className="block" initial={{ y: "110%" }} animate={{ y: "0%" }} transition={{ duration: 1, delay: 0.55, ease: [0.16, 1, 0.3, 1] }}>
              {project.headlineA}
            </motion.span>
          </span>
          <span className="line-mask">
            <motion.span className="block italic text-gold" initial={{ y: "110%" }} animate={{ y: "0%" }} transition={{ duration: 1, delay: 0.7, ease: [0.16, 1, 0.3, 1] }}>
              {project.headlineB}
            </motion.span>
          </span>
        </h1>

        <div className="mt-8 md:mt-10 md:flex md:items-end md:justify-between gap-8">
          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.8 }}
            className="max-w-xl text-sm md:text-base font-normal text-[#F9F8F6]/85 leading-relaxed"
          >
            {project.tagline}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.3, duration: 0.8 }}
            className="mt-8 md:mt-0 flex flex-col sm:flex-row gap-4 shrink-0"
          >
            <button
              data-testid="hero-cost-sheet-button"
              onClick={() => openPopup({ project, intent: "Get Cost Sheet" })}
              className="w-full sm:w-auto text-center bg-gold text-ink text-[12px] md:text-[13px] font-semibold uppercase tracking-[0.18em] px-8 py-4 hover:bg-[#F9F8F6] transition-colors duration-300"
            >
              Get Cost Sheet
            </button>
            <button
              data-testid="hero-site-visit-button"
              onClick={() => scrollToId("enquire")}
              className="w-full sm:w-auto text-center border border-[#F9F8F6]/40 text-[#F9F8F6] text-[12px] md:text-[13px] font-medium uppercase tracking-[0.18em] px-8 py-4 hover:border-gold hover:text-gold transition-colors duration-300"
            >
              Book Site Visit
            </button>
          </motion.div>
        </div>
      </div>

      <div className="absolute bottom-8 right-5 md:right-10 z-10 hidden sm:flex items-center gap-3 text-[#F9F8F6]/50">
        <span className="text-[10px] uppercase tracking-editorial">Scroll</span>
        <motion.span animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}>
          <ArrowDown size={14} />
        </motion.span>
      </div>
      <div className="absolute bottom-8 left-5 md:left-10 z-10 flex gap-2">
        {project.heroImages.map((_, i) => (
          <button
            key={i}
            data-testid={`hero-slide-dot-${i}`}
            onClick={() => setSlide(i)}
            aria-label={`Slide ${i + 1}`}
            className={`h-[2px] transition-all duration-500 ${i === slide ? "w-10 bg-gold" : "w-5 bg-white/30"}`}
          />
        ))}
      </div>
    </section>
  );
}

export default function ProjectPage({ project, other }) {
  const { openPopup } = useEnquiry();
  const [showAllGallery, setShowAllGallery] = useState(false);
  const gallery = showAllGallery ? project.gallery : project.gallery.slice(0, 6);

  return (
    <main key={project.slug}>
      <ProjectHero project={project} />

      <section data-testid="stats-strip" className="bg-ink border-y border-gold/20">
        <div className="max-w-7xl mx-auto px-5 md:px-10 grid grid-cols-2 md:grid-cols-4">
          {project.stats.map(([v, l], i) => (
            <FadeUp key={l} delay={i * 0.08}>
              <div data-testid={`stat-${i}`} className="py-9 md:py-12 pr-6">
                <p className="font-serif-display text-3xl md:text-5xl text-gold">{v}</p>
                <p className="mt-2 text-[10px] md:text-xs uppercase tracking-[0.18em] text-[#F9F8F6]/60">{l}</p>
              </div>
            </FadeUp>
          ))}
        </div>
      </section>

      <section id="overview" data-testid="overview-section" className="bg-[#F9F8F6] py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-5 md:px-10 grid md:grid-cols-12 gap-12">
          <div className="md:col-span-7">
            <FadeUp><Eyebrow>Project Overview</Eyebrow></FadeUp>
            <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl leading-[1.15] text-ink font-medium">
              <MaskedLine delay={0.05}>One address.</MaskedLine>
              <MaskedLine delay={0.15}><span className="italic text-[#8B7355]">Everything included.</span></MaskedLine>
            </h2>
            {project.overview.map((p, i) => (
              <FadeUp key={i} delay={0.2 + i * 0.08}>
                <p className="mt-6 text-sm md:text-base font-normal text-[#33404f] leading-relaxed">{p}</p>
              </FadeUp>
            ))}
          </div>
          <div className="md:col-span-5">
            <FadeUp delay={0.15}>
              <div className="border border-[#e0d9cc] bg-white divide-y divide-[#efe9de]">
                {project.facts.map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-4 px-5 md:px-6 py-4">
                    <span className="text-[11px] uppercase tracking-[0.16em] text-[#8B7355] pt-0.5 shrink-0">{k}</span>
                    <span className="text-sm font-medium text-ink text-right [overflow-wrap:anywhere] min-w-0">{v}</span>
                  </div>
                ))}
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      <section id="price" data-testid="price-section" className="bg-ink py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <div className="grid md:grid-cols-12 gap-8 mb-12 md:mb-16">
            <div className="md:col-span-7">
              <FadeUp><Eyebrow dark>Price & Configuration</Eyebrow></FadeUp>
              <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl leading-[1.15] text-[#F9F8F6] font-medium">
                <MaskedLine delay={0.05}>Homes from</MaskedLine>
                <MaskedLine delay={0.15}><span className="italic text-gold">{project.priceTag}</span></MaskedLine>
              </h2>
            </div>
            <div className="md:col-span-5 flex items-end">
              <FadeUp delay={0.2}>
                <p className="text-sm font-normal text-[#F9F8F6]/70 leading-relaxed">{project.pricingNote}</p>
              </FadeUp>
            </div>
          </div>

          <div className="border-t border-[#F9F8F6]/12">
            {project.pricing.map(([config, size, price], i) => (
              <FadeUp key={config} delay={i * 0.04}>
                <div data-testid={`price-row-${i}`} className="grid grid-cols-2 md:grid-cols-4 items-center gap-3 md:gap-6 py-6 border-b border-[#F9F8F6]/12 group">
                  <p className="font-serif-display text-xl md:text-2xl text-[#F9F8F6]">{config}</p>
                  <p className="text-xs md:text-sm font-normal text-[#F9F8F6]/60 text-right md:text-left">{size}</p>
                  <p className="text-sm md:text-base font-medium text-gold">{price}</p>
                  <div className="text-right">
                    <button
                      data-testid={`price-cost-sheet-${i}`}
                      onClick={() => openPopup({ project, config, intent: "Get Cost Sheet" })}
                      className="text-[11px] font-semibold uppercase tracking-[0.16em] border border-gold/50 text-gold px-4 md:px-6 py-2.5 hover:bg-gold hover:text-ink transition-colors duration-300"
                    >
                      Cost Sheet
                    </button>
                  </div>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      <section id="gallery" data-testid="gallery-section" className="bg-[#F9F8F6] py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <div className="mb-12 md:mb-16">
            <FadeUp><Eyebrow>Gallery</Eyebrow></FadeUp>
            <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl leading-[1.15] text-ink font-medium">
              <MaskedLine delay={0.05}>Inside</MaskedLine>
              <MaskedLine delay={0.15}><span className="italic text-[#8B7355]">{project.name}.</span></MaskedLine>
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
            {gallery.map(([src, label], i) => (
              <FadeUp key={src} delay={(i % 3) * 0.06} className={i === 0 ? "col-span-2 row-span-2" : ""}>
                <div data-testid={`gallery-img-${i}`} className="group relative overflow-hidden bg-ink h-full">
                  <img
                    src={src}
                    alt={`${project.name} — ${label}`}
                    loading="lazy"
                    className={`w-full object-cover transition-transform duration-[1.4s] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-[1.06] ${i === 0 ? "aspect-square md:aspect-auto md:h-full" : "aspect-square"}`}
                  />
                  <div className="absolute inset-0 bg-ink/0 transition-colors duration-500 group-hover:bg-ink/30" />
                  <span className="absolute bottom-0 left-0 right-0 px-4 py-3 text-[10px] md:text-[11px] uppercase tracking-[0.16em] text-[#F9F8F6] bg-gradient-to-t from-ink/90 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                    {label}
                  </span>
                </div>
              </FadeUp>
            ))}
          </div>
          {project.gallery.length > 6 && (
            <FadeUp className="mt-8 text-center">
              <button
                data-testid="gallery-toggle-button"
                onClick={() => setShowAllGallery(!showAllGallery)}
                className="text-[12px] font-semibold uppercase tracking-[0.2em] text-ink border border-ink/30 px-8 py-4 hover:bg-ink hover:text-[#F9F8F6] transition-colors duration-300"
              >
                {showAllGallery ? "Show fewer photos" : `View all ${project.gallery.length} photos`}
              </button>
            </FadeUp>
          )}
        </div>
      </section>

      <section id="plans" data-testid="plans-section" className="bg-[#F9F8F6] pb-24 md:pb-32">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <div className="mb-10 md:mb-14 flex flex-wrap items-end justify-between gap-6">
            <div>
              <FadeUp><Eyebrow>Master & Floor Plans</Eyebrow></FadeUp>
              <h2 className="font-serif-display text-3xl sm:text-4xl leading-[1.15] text-ink font-medium">
                <MaskedLine delay={0.05}>Every square foot,</MaskedLine>
                <MaskedLine delay={0.15}><span className="italic text-[#8B7355]">accounted for.</span></MaskedLine>
              </h2>
            </div>
            <FadeUp delay={0.15}>
              <button
                data-testid="plans-brochure-button"
                onClick={() => openPopup({ project, intent: "Get Brochure & Floor Plans" })}
                className="bg-ink text-[#F9F8F6] text-[12px] font-semibold uppercase tracking-[0.18em] px-8 py-4 hover:bg-gold hover:text-ink transition-colors duration-300"
              >
                Get Full Brochure
              </button>
            </FadeUp>
          </div>
          <div className="flex gap-4 md:gap-5 overflow-x-auto pb-4 snap-x snap-mandatory" data-testid="plans-scroller">
            {project.plans.map(([src, label]) => (
              <div key={label} className="snap-start shrink-0 w-[78vw] sm:w-[420px] border border-[#e0d9cc] bg-white">
                <div className="h-[240px] sm:h-[280px] overflow-hidden flex items-center justify-center p-4">
                  <img src={src} alt={`${project.name} — ${label}`} loading="lazy" className="max-h-full w-auto object-contain" />
                </div>
                <p className="px-5 py-4 border-t border-[#efe9de] text-[11px] font-medium uppercase tracking-[0.16em] text-[#8B7355]">{label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="amenities" data-testid="amenities-section" className="bg-ink py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <div className="mb-12 md:mb-16">
            <FadeUp><Eyebrow dark>Amenities</Eyebrow></FadeUp>
            <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl leading-[1.15] text-[#F9F8F6] font-medium">
              <MaskedLine delay={0.05}>{project.amenities.length}+ ways</MaskedLine>
              <MaskedLine delay={0.15}><span className="italic text-gold">to spend a Sunday.</span></MaskedLine>
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-x-8">
            {project.amenities.map((a, i) => (
              <FadeUp key={a} delay={(i % 3) * 0.05}>
                <div data-testid={`amenity-${i}`} className="flex items-baseline gap-4 py-5 border-b border-[#F9F8F6]/10">
                  <span className="font-serif-display italic text-gold text-lg shrink-0">{String(i + 1).padStart(2, "0")}</span>
                  <span className="text-sm md:text-base font-normal text-[#F9F8F6]/85">{a}</span>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      <section id="location" data-testid="location-section" className="bg-[#F9F8F6] py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-5 md:px-10 grid md:grid-cols-12 gap-12">
          <div className="md:col-span-6">
            <FadeUp><Eyebrow>Location</Eyebrow></FadeUp>
            <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl leading-[1.15] text-ink font-medium">
              <MaskedLine delay={0.05}>{project.location.title.split(",")[0]},</MaskedLine>
              <MaskedLine delay={0.15}><span className="italic text-[#8B7355]">{project.location.title.split(",").slice(1).join(",").trim() || "Bengaluru"}.</span></MaskedLine>
            </h2>
            <FadeUp delay={0.2}>
              <p className="mt-7 text-sm md:text-base font-normal text-[#33404f] leading-relaxed">{project.location.copy}</p>
            </FadeUp>
            <FadeUp delay={0.3}>
              <a
                data-testid="location-map-link"
                href={project.location.mapUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 mt-8 bg-ink text-[#F9F8F6] text-[12px] font-semibold uppercase tracking-[0.18em] px-8 py-4 hover:bg-gold hover:text-ink transition-colors duration-300"
              >
                <MapPin size={15} strokeWidth={1.5} /> Open in Google Maps
              </a>
            </FadeUp>
          </div>
          <div className="md:col-span-6">
            <FadeUp delay={0.15}>
              <div className="border border-[#e0d9cc] bg-white divide-y divide-[#efe9de]">
                {project.location.points.map(([place, dist]) => (
                  <div key={place} className="flex justify-between gap-6 px-6 py-4">
                    <span className="text-sm font-normal text-ink">{place}</span>
                    <span className="text-[11px] uppercase tracking-[0.16em] text-[#8B7355] pt-0.5 text-right shrink-0">{dist}</span>
                  </div>
                ))}
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      <section id="faq" data-testid="faq-section" className="bg-[#F9F8F6] pb-24 md:pb-32">
        <div className="max-w-7xl mx-auto px-5 md:px-10 grid md:grid-cols-12 gap-12">
          <div className="md:col-span-4">
            <FadeUp><Eyebrow>FAQ</Eyebrow></FadeUp>
            <h2 className="font-serif-display text-3xl sm:text-4xl leading-[1.15] text-ink font-medium">
              <MaskedLine delay={0.05}>Asked often,</MaskedLine>
              <MaskedLine delay={0.15}><span className="italic text-[#8B7355]">answered honestly.</span></MaskedLine>
            </h2>
          </div>
          <div className="md:col-span-8">
            <FadeUp delay={0.15}>
              <Accordion type="single" collapsible data-testid="faq-accordion" className="border-t border-[#e0d9cc]">
                {project.faqs.map((f, i) => (
                  <AccordionItem key={i} value={`faq-${i}`} className="border-b border-[#e0d9cc]">
                    <AccordionTrigger data-testid={`faq-trigger-${i}`} className="text-left font-serif-display text-lg md:text-xl text-ink hover:text-[#8B7355] hover:no-underline py-6 transition-colors">
                      {f.q}
                    </AccordionTrigger>
                    <AccordionContent data-testid={`faq-content-${i}`} className="text-sm md:text-base font-normal text-[#33404f] leading-relaxed pb-6">
                      {f.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </FadeUp>
          </div>
        </div>
      </section>

      <section id="enquire" data-testid="enquiry-section" className="bg-ink py-24 md:py-32 border-t border-gold/20">
        <div className="max-w-7xl mx-auto px-5 md:px-10 grid md:grid-cols-12 gap-12 md:gap-16">
          <div className="md:col-span-5">
            <FadeUp><Eyebrow dark>Private Consultation</Eyebrow></FadeUp>
            <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl leading-[1.15] text-[#F9F8F6] font-medium">
              <MaskedLine delay={0.05}>See {project.name}</MaskedLine>
              <MaskedLine delay={0.15}><span className="italic text-gold">in person.</span></MaskedLine>
            </h2>
            <FadeUp delay={0.25}>
              <p className="mt-8 text-sm md:text-base font-normal text-[#F9F8F6]/75 leading-relaxed max-w-md">
                Share your details for the latest pricing, cost sheet, floor plans and a guided site
                visit — an advisor usually responds within a few hours.
              </p>
            </FadeUp>
            <FadeUp delay={0.35}>
              <div className="mt-10 space-y-5">
                <a data-testid="contact-phone-link" href={CONTACT.phoneHref} className="flex items-center gap-4 text-[#F9F8F6]/90 hover:text-gold transition-colors">
                  <span className="w-10 h-10 border border-[#F9F8F6]/20 flex items-center justify-center shrink-0"><Phone size={15} strokeWidth={1.5} /></span>
                  <span className="text-sm font-normal tracking-wide">{CONTACT.phone}</span>
                </a>
                <a data-testid="contact-email-link" href={`mailto:${CONTACT.email}`} className="flex items-center gap-4 text-[#F9F8F6]/90 hover:text-gold transition-colors">
                  <span className="w-10 h-10 border border-[#F9F8F6]/20 flex items-center justify-center shrink-0"><Mail size={15} strokeWidth={1.5} /></span>
                  <span className="text-sm font-normal tracking-wide">{CONTACT.email}</span>
                </a>
                <a data-testid="contact-whatsapp-link" href={CONTACT.whatsapp} target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 text-[#F9F8F6]/90 hover:text-gold transition-colors">
                  <span className="w-10 h-10 border border-[#F9F8F6]/20 flex items-center justify-center shrink-0"><MessageCircle size={15} strokeWidth={1.5} /></span>
                  <span className="text-sm font-normal tracking-wide">Chat on WhatsApp</span>
                </a>
              </div>
            </FadeUp>
          </div>
          <div className="md:col-span-7">
            <FadeUp delay={0.2}>
              <div className="border border-[#F9F8F6]/12 bg-[#F9F8F6]/[0.03] p-7 md:p-12">
                <LeadForm project={project} intent="Request a Callback" />
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      {other && (
        <section data-testid="other-project-strip" className="bg-[#F9F8F6] py-20 md:py-28">
          <div className="max-w-7xl mx-auto px-5 md:px-10">
            <FadeUp>
              <p className="text-[11px] uppercase tracking-editorial text-[#8B7355] mb-8">Also from the Sobha portfolio</p>
            </FadeUp>
            <FadeUp delay={0.1}>
              <Link
                to={`/${other.slug}`}
                data-testid="other-project-card"
                className="group grid md:grid-cols-2 bg-ink border border-ink"
              >
                <div className="relative overflow-hidden aspect-[16/9] md:aspect-auto md:min-h-[380px]">
                  <img
                    src={other.heroImages[0]}
                    alt={other.name}
                    loading="lazy"
                    className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1.4s] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-[1.05]"
                  />
                  <div className="absolute inset-0 bg-ink/10 transition-colors duration-500 group-hover:bg-ink/30" />
                </div>
                <div className="p-8 md:p-14 flex flex-col justify-center">
                  <p className="text-[11px] uppercase tracking-editorial text-gold mb-4">{other.eyebrow}</p>
                  <h3 className="font-serif-display text-3xl md:text-4xl text-[#F9F8F6] font-medium">{other.name}</h3>
                  <p className="mt-4 text-sm font-normal text-[#F9F8F6]/70 leading-relaxed max-w-md">{other.tagline}</p>
                  <span className="mt-8 inline-flex items-center gap-3 text-[12px] font-semibold uppercase tracking-[0.2em] text-gold">
                    Explore {other.name} <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
                  </span>
                </div>
              </Link>
            </FadeUp>
          </div>
        </section>
      )}
    </main>
  );
}
